/**
 * orgService — Team workspaces (V2 additive) client.
 *
 * Wraps the three org lambdas:
 *   /v2-org         org CRUD + members/roles + usage
 *   /v2-org-invite  email invites (send/list/revoke/accept/pending-for-me)
 *   /v2-org-billing Stripe (team checkout / plan updates / pooled top-ups / portal)
 *
 * Kept separate from v2Service so the generation client stays focused; same
 * Bearer-token attach. These endpoints are org-explicit (org_id in the call),
 * so the workspace interceptor is NOT registered here.
 */
import axios, { AxiosInstance } from 'axios';
import { API_CONFIG } from './config';
import { installBanInterceptor } from './banInterceptor';
import { getIdToken } from '@/auth/firebase';
import { parseOrganization, type Organization, type OrganizationResponse } from '@/types';

const E = API_CONFIG.endpoints;

export interface OrgMember {
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  email?: string;
  display_name?: string;
  joined_at?: string;
}

export interface OrgInvite {
  invite_token: string;
  org_id: string;
  org_name?: string;
  email: string;
  role: 'admin' | 'member';
  invite_status: string;
  invited_by_name?: string;
  created_date?: string;
  expires_at?: string;
}

export interface OrgUsage {
  org_id: string;
  days: number;
  total_credits: number;
  total_orders: number;
  remaining_credit: number;
  weekly_credits: number;
  by_member: { user_id: string; display_name?: string; credits: number; orders: number }[];
  by_day: { day: string; credits: number }[];
}

export interface OrgDetails extends Organization {
  members: OrgMember[];
  memberCount: number;
}

export interface TeamCheckoutSession {
  session_id: string;
  client_secret?: string;
  checkout_url?: string;
}

class OrgService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.baseUrl,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    });
    this.client.interceptors.request.use(async (config) => {
      const token = await getIdToken();
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }
      return config;
    });
    // Block/ban: surface 403 user_banned globally + tear down the session.
    installBanInterceptor(this.client);
  }

  // ---------- Org CRUD + members ----------

  async createOrg(userId: string, name: string): Promise<Organization> {
    const { data } = await this.client.post<OrganizationResponse>(E.v2Org, {
      user_id: userId, action: 'create', name,
    });
    return parseOrganization(data);
  }

  async listOrgs(userId: string): Promise<Organization[]> {
    const { data } = await this.client.post<{ organizations: OrganizationResponse[] }>(E.v2Org, {
      user_id: userId, action: 'list',
    });
    return (data.organizations || []).map(parseOrganization);
  }

  async getOrg(userId: string, orgId: string): Promise<OrgDetails> {
    const { data } = await this.client.post<OrganizationResponse & { members: OrgMember[]; member_count: number }>(
      E.v2Org, { user_id: userId, action: 'get', org_id: orgId });
    return { ...parseOrganization(data), members: data.members || [], memberCount: data.member_count || 0 };
  }

  async renameOrg(userId: string, orgId: string, name: string): Promise<void> {
    await this.client.post(E.v2Org, { user_id: userId, action: 'rename', org_id: orgId, name });
  }

  async setMemberRole(userId: string, orgId: string, memberUserId: string, role: 'admin' | 'member'): Promise<void> {
    await this.client.post(E.v2Org, {
      user_id: userId, action: 'set_role', org_id: orgId, member_user_id: memberUserId, role,
    });
  }

  async removeMember(userId: string, orgId: string, memberUserId: string): Promise<void> {
    await this.client.post(E.v2Org, {
      user_id: userId, action: 'remove', org_id: orgId, member_user_id: memberUserId,
    });
  }

  async leaveOrg(userId: string, orgId: string): Promise<void> {
    await this.client.post(E.v2Org, { user_id: userId, action: 'leave', org_id: orgId });
  }

  async getUsage(userId: string, orgId: string, days = 30): Promise<OrgUsage> {
    const { data } = await this.client.post<OrgUsage>(E.v2Org, {
      user_id: userId, action: 'usage', org_id: orgId, days,
    });
    return data;
  }

  // ---------- Invites ----------

  async sendInvite(userId: string, orgId: string, email: string, role: 'admin' | 'member' = 'member'): Promise<OrgInvite> {
    const { data } = await this.client.post<OrgInvite>(E.v2OrgInvite, {
      user_id: userId, action: 'send', org_id: orgId, email, role,
    });
    return data;
  }

  async listInvites(userId: string, orgId: string): Promise<OrgInvite[]> {
    const { data } = await this.client.post<{ invites: OrgInvite[] }>(E.v2OrgInvite, {
      user_id: userId, action: 'list', org_id: orgId,
    });
    return data.invites || [];
  }

  async revokeInvite(userId: string, orgId: string, inviteToken: string): Promise<void> {
    await this.client.post(E.v2OrgInvite, {
      user_id: userId, action: 'revoke', org_id: orgId, invite_token: inviteToken,
    });
  }

  async pendingInvitesForMe(userId: string): Promise<OrgInvite[]> {
    const { data } = await this.client.post<{ invites: OrgInvite[] }>(E.v2OrgInvite, {
      user_id: userId, action: 'pending_for_me',
    });
    return data.invites || [];
  }

  async acceptInvite(userId: string, inviteToken: string): Promise<{ org_id: string; org_name?: string; joined: boolean; already_member?: boolean }> {
    const { data } = await this.client.post(E.v2OrgInvite, {
      user_id: userId, action: 'accept', invite_token: inviteToken,
    });
    return data;
  }

  // ---------- Billing ----------

  async teamCheckout(opts: {
    userId: string; orgId: string;
    billingPeriod: 'weekly' | 'yearly'; seats: number; credits: number;
    uiMode?: 'embedded'; successUrl?: string; cancelUrl?: string; returnUrl?: string;
  }): Promise<TeamCheckoutSession> {
    const { data } = await this.client.post<TeamCheckoutSession>(E.v2OrgBilling, {
      user_id: opts.userId, action: 'checkout', org_id: opts.orgId,
      billing_period: opts.billingPeriod, seats: opts.seats, credits: opts.credits,
      ui_mode: opts.uiMode, success_url: opts.successUrl, cancel_url: opts.cancelUrl,
      return_url: opts.returnUrl,
    });
    return data;
  }

  async updateTeamPlan(userId: string, orgId: string, opts: { seats?: number; credits?: number }): Promise<{ seats: number; credits: number }> {
    const { data } = await this.client.post(E.v2OrgBilling, {
      user_id: userId, action: 'update_plan', org_id: orgId,
      seats: opts.seats, credits: opts.credits,
    });
    return data;
  }

  async teamTopUp(opts: {
    userId: string; orgId: string; creditAmount: number;
    uiMode?: 'embedded'; successUrl?: string; cancelUrl?: string; returnUrl?: string;
  }): Promise<TeamCheckoutSession> {
    const { data } = await this.client.post<TeamCheckoutSession>(E.v2OrgBilling, {
      user_id: opts.userId, action: 'topup', org_id: opts.orgId,
      credit_amount: opts.creditAmount,
      ui_mode: opts.uiMode, success_url: opts.successUrl, cancel_url: opts.cancelUrl,
      return_url: opts.returnUrl,
    });
    return data;
  }

  async billingPortal(userId: string, orgId: string, returnUrl?: string): Promise<string> {
    const { data } = await this.client.post<{ portal_url: string }>(E.v2OrgBilling, {
      user_id: userId, action: 'portal', org_id: orgId, return_url: returnUrl,
    });
    return data.portal_url;
  }
}

export const orgService = new OrgService();
export default orgService;
